
import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DayPicker } from "react-day-picker";
import { Calendar as CalendarIcon, Users, Image } from "lucide-react";

interface EventDetailsFormProps {
  eventName: string;
  setEventName: (value: string) => void;
  eventType: string;
  setEventType: (value: string) => void;
  eventDate: Date | undefined;
  setEventDate: (date: Date | undefined) => void;
  startTime: string;
  setStartTime: (value: string) => void;
  endTime: string;
  setEndTime: (value: string) => void;
  guestCount: number;
  setGuestCount: (value: number) => void;
  printQuantity: number;
  setPrintQuantity: (value: number) => void;
  eventDescription: string;
  setEventDescription: (value: string) => void;
}

const EventDetailsForm: React.FC<EventDetailsFormProps> = ({
  eventName,
  setEventName,
  eventType,
  setEventType,
  eventDate,
  setEventDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  guestCount,
  setGuestCount,
  printQuantity,
  setPrintQuantity,
  eventDescription,
  setEventDescription,
}) => {
  const handleGuestCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string during typing
    if (value === '') {
      setGuestCount(0);
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        setGuestCount(numValue);
      }
    }
  };

  const handlePrintQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string during typing
    if (value === '') {
      setPrintQuantity(0);
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        setPrintQuantity(numValue);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="event-name">Event Name</Label>
        <Input
          type="text"
          id="event-name"
          placeholder="e.g., Birthday Party, Corporate Event"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="event-type">Event Type</Label>
        <Input
          type="text"
          id="event-type"
          placeholder="e.g., Celebration, Promotion, Team Building"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
        />
      </div>
      <div>
        <Label>Event Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[240px] justify-start text-left font-normal",
                !eventDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {eventDate ? format(eventDate, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <DayPicker
              mode="single"
              selected={eventDate}
              onSelect={setEventDate}
              disabled={{ before: new Date() }}
              className="border-none shadow-none"
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start-time">Start Time</Label>
          <Input
            type="time"
            id="start-time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="end-time">End Time</Label>
          <Input
            type="time"
            id="end-time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="guest-count">Party Size</Label>
          <div className="relative">
            <Input
              type="number"
              id="guest-count"
              min="0"
              placeholder="15"
              value={guestCount === 0 ? '' : guestCount.toString()}
              onChange={handleGuestCountChange}
            />
            <Users className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
          </div>
        </div>
        <div>
          <Label htmlFor="print-quantity">Print Quantity</Label>
          <div className="relative">
            <Input
              type="number"
              id="print-quantity"
              min="0"
              placeholder="20"
              value={printQuantity === 0 ? '' : printQuantity.toString()}
              onChange={handlePrintQuantityChange}
            />
            <Image className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor="event-description">Event Description</Label>
        <Textarea
          id="event-description"
          placeholder="Tell us more about your event"
          className="resize-none h-24"
          value={eventDescription}
          onChange={(e) => setEventDescription(e.target.value)}
        />
      </div>
    </div>
  );
};

export default EventDetailsForm;
